import * as React from "react";
import { Member } from "../context/AdContext";
import { midpointApiService } from "../services/midpointApi";
import { MidpointAuthenticationError } from "../services/midpointAuth";
import Loader from "./Loader";

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMemberAdded: () => void;
  groupId: string;
  groupName: string;
  existingMembers: Member[];
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onMemberAdded,
  groupId,
  groupName,
  existingMembers
}) => {
  const [allUsers, setAllUsers] = React.useState<Member[]>([]);
  const [filteredUsers, setFilteredUsers] = React.useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<Member | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const useMidpoint = process.env.REACT_APP_USE_MIDPOINT === 'true';

  // Fetch all users when modal opens
  React.useEffect(() => {
    if (isOpen && allUsers.length === 0) {
      fetchAllUsers();
    }
  }, [isOpen]);

  // Filter users based on search term and exclude existing members
  React.useEffect(() => {
    const existingEmailSet = new Set(existingMembers.map(m => m.userPrincipalName));
    const availableUsers = allUsers.filter(
      user => !existingEmailSet.has(user.userPrincipalName)
    );

    if (!searchTerm) {
      setFilteredUsers(availableUsers);
    } else {
      const filtered = availableUsers.filter(user =>
        user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userPrincipalName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, allUsers, existingMembers]);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (useMidpoint) {
        const users = await midpointApiService.getAllUsers();
        setAllUsers(users);
      } else {
        // Legacy API doesn't have an endpoint to fetch all users
        // This functionality is only available with Midpoint API
        setError('Fetching all users is only available with the live API. Please contact your administrator to enable integration.');
        setAllUsers([]);
      }
    } catch (error: unknown) {
      let errorMessage = 'Failed to load users';
      if (error instanceof MidpointAuthenticationError) {
        errorMessage = `Authentication Error: ${error.message}`;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;

    if (!useMidpoint) {
      setError('Adding members is only available with the live API. Please contact your administrator to enable integration.');
      return;
    }

    try {
      setAdding(true);
      setError(null);

      await midpointApiService.addMemberToGroup(selectedUser.id, groupId);

      // Reset form
      setSelectedUser(null);
      setSearchTerm("");
      setIsDropdownOpen(false);

      // Notify parent component
      onMemberAdded();
      onClose();
    } catch (error: unknown) {
      let errorMessage = 'Failed to add member';

      if (error instanceof MidpointAuthenticationError) {
        errorMessage = `Authentication Error: ${error.message}`;
      } else if (error instanceof Error) {
        if (error.message.includes('Resource not found')) {
          errorMessage = 'Unable to assign role. Please check if the user and role exist and you have sufficient permissions.';
        } else if (error.message.includes('configuration issue')) {
          errorMessage = error.message;
        } else {
          errorMessage = `Assignment failed: ${error.message}`;
        }
      }

      console.error('Full error details:', error);
      setError(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  const handleUserSelect = (user: Member) => {
    setSelectedUser(user);
    setSearchTerm(user.displayName);
    setIsDropdownOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedUser(null);
    setIsDropdownOpen(true);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "24px",
        minWidth: "600px",
        maxWidth: "700px",
        width: "90vw",
        maxHeight: "85vh",
        minHeight: "500px",
        overflow: "auto",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}>
          <h2 style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#2D3748",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            Add Member to {groupName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#A0AEC0",
              padding: "4px"
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "#B91C1C",
            fontSize: "14px"
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Loader />
            <div style={{
              marginTop: "16px",
              fontSize: "14px",
              color: "#718096"
            }}>
              Loading users from repository and connected sources...
            </div>
          </div>
        ) : (
          <div>
            {allUsers.length > 0 && (
              <div style={{
                backgroundColor: "#F7FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                padding: "8px 12px",
                marginBottom: "16px",
                fontSize: "12px",
                color: "#4A5568"
              }}>
                Found {allUsers.length} total users
                ({allUsers.filter(u => !u.id.startsWith('shadow-')).length} from repository, {allUsers.filter(u => u.id.startsWith('shadow-')).length} from external sources)
                • {filteredUsers.length} available to add
              </div>
            )}

            <div style={{ position: "relative", marginBottom: "20px" }}>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151"
              }}>
                Select User
              </label>

              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Search users..."
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #E1E7F0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box"
                }}
              />

              {isDropdownOpen && filteredUsers.length > 0 && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #E1E7F0",
                  borderRadius: "6px",
                  marginTop: "4px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  zIndex: 1001,
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                }}>
                  {filteredUsers.slice(0, 10).map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      style={{
                        padding: "12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #F7FAFC",
                        fontSize: "14px",
                        backgroundColor: "white"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F7FAFC";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                        <div style={{ fontWeight: "500", color: "#2D3748" }}>
                          {user.displayName}
                        </div>
                        <span style={{
                          fontSize: "8px",
                          backgroundColor: user.id.startsWith('shadow-') ? "#F59E0B" : "#22C55E",
                          color: "white",
                          padding: "2px 4px",
                          borderRadius: "3px"
                        }}>
                          {user.id.startsWith('shadow-') ? "EXTERNAL" : "REPOSITORY"}
                        </span>
                      </div>
                      <div style={{ color: "#718096", fontSize: "12px" }}>
                        {user.userPrincipalName}
                      </div>
                      {user.jobTitle && (
                        <div style={{ color: "#A0AEC0", fontSize: "12px" }}>
                          {user.jobTitle}
                        </div>
                      )}
                      {user.id.startsWith('shadow-') && (
                        <div style={{
                          color: "#F59E0B",
                          fontSize: "11px",
                          fontStyle: "italic",
                          marginTop: "4px"
                        }}>
                          Will be imported from external source
                        </div>
                      )}
                    </div>
                  ))}

                  {filteredUsers.length > 10 && (
                    <div style={{
                      padding: "8px 12px",
                      fontSize: "12px",
                      color: "#718096",
                      textAlign: "center",
                      borderTop: "1px solid #E2E8F0"
                    }}>
                      {filteredUsers.length - 10} more users... (refine search)
                    </div>
                  )}
                </div>
              )}

              {searchTerm && filteredUsers.length === 0 && !loading && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #E1E7F0",
                  borderRadius: "6px",
                  marginTop: "4px",
                  padding: "12px",
                  textAlign: "center",
                  color: "#718096",
                  fontSize: "14px"
                }}>
                  No users found
                </div>
              )}
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px"
            }}>
              <button
                onClick={onClose}
                disabled={adding}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #E1E7F0",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: adding ? "not-allowed" : "pointer",
                  opacity: adding ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={!selectedUser || adding}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: selectedUser && !adding ? "#3B82F6" : "#E1E7F0",
                  color: selectedUser && !adding ? "white" : "#9CA3AF",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: selectedUser && !adding ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                {adding && (
                  <div style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff40",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }} />
                )}
                {adding ? "Adding..." : "Add Member"}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AddMemberModal;